import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Building2, Phone, MapPin, Info, Image as ImageIcon, ArrowLeft, Save, CheckCircle, Loader } from 'lucide-react';

const TempleEditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const TEMPLE_PROFILE_KEY = "temple_profile";

  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    templeName: '',
    templeType: '',
    mainDeityName: '',
    yearEstablished: '',
    contactPersonName: '',
    mobileNumber: '',
    emailAddress: '',
    fullAddress: '',
    city: '',
    state: '',
    pincode: '',
    googleMapLocation: '',
    templeDescription: '',
    darshanTimings: '',
    specialSevas: '',
    templeRules: '',
    profileImage: null,
    galleryImages: []
  });

  const [existingProfileImage, setExistingProfileImage] = useState(null);
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  const [profilePreview, setProfilePreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [errors, setErrors] = useState({});

  // Fetch existing temple data on mount
  useEffect(() => {
    fetchTempleData();
  }, []);


  const fetchTempleData = () => {
  console.log("📥 Loading temple profile for edit");

  const storedProfile = localStorage.getItem(TEMPLE_PROFILE_KEY);

  if (!storedProfile) {
    alert("No temple profile found");
    navigate("/temple-dashboard");
    return;
  }

  const data = JSON.parse(storedProfile);
  console.log("📝 Profile loaded:", data);

  setFormData({
    ...data,
    profileImage: null,
    galleryImages: [],
  });

  setLoading(false);
};


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProfileImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Please upload an image less than 5MB');
        return;
      }
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        alert('Please upload only JPG or PNG images');
        return;
      }

      setFormData(prev => ({ ...prev, profileImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is larger than 5MB`);
        return false;
      }
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        alert(`${file.name} is not a JPG or PNG file`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 10) {
      alert('You can only upload up to 10 images');
      validFiles.splice(10);
    }

    setFormData(prev => ({ ...prev, galleryImages: validFiles }));
    
    const previews = [];
    let loadedCount = 0;
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result);
        loadedCount++;
        if (loadedCount === validFiles.length) {
          setGalleryPreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.templeName.trim()) newErrors.templeName = 'Temple name is required';
    if (!formData.templeType) newErrors.templeType = 'Temple type is required';
    if (!formData.mainDeityName.trim()) newErrors.mainDeityName = 'Main deity name is required';
    if (!formData.yearEstablished.trim()) newErrors.yearEstablished = 'Year established is required';
    if (formData.yearEstablished && !/^\d{4}$/.test(formData.yearEstablished)) {
      newErrors.yearEstablished = 'Please enter a valid 4-digit year';
    }
    
    if (!formData.contactPersonName.trim()) newErrors.contactPersonName = 'Contact person name is required';
    if (!formData.mobileNumber.trim()) newErrors.mobileNumber = 'Mobile number is required';
    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }
    if (!formData.emailAddress.trim()) newErrors.emailAddress = 'Email address is required';
    if (formData.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }
    
    if (!formData.fullAddress.trim()) newErrors.fullAddress = 'Full address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }
    
    if (!formData.templeDescription.trim()) newErrors.templeDescription = 'Temple description is required';
    if (!formData.darshanTimings.trim()) newErrors.darshanTimings = 'Darshan timings are required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = () => {
  console.log("💾 Updating temple profile");

  const updatedProfile = {
    ...formData,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(
    TEMPLE_PROFILE_KEY,
    JSON.stringify(updatedProfile)
  );

  console.log("✅ Profile updated:", updatedProfile);
  alert("Temple profile updated successfully!");

  navigate("/temple-dashboard");
};


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-orange-600 font-semibold text-lg">Loading temple data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/temple-dashboard')}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 text-orange-700 hover:text-orange-800 font-semibold transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border-2 border-green-300 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <p className="text-green-800 font-semibold">{successMessage}</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full mb-6 shadow-lg">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-orange-800 mb-3">
            Edit Temple Profile
          </h1>
          <p className="text-gray-600 text-lg">
            Update your temple information to keep your profile current
          </p>
        </div>

        {/* Basic Details Section */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-5 border-b border-orange-200">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-orange-700" />
              <div>
                <h2 className="text-xl font-bold text-orange-900">Basic Details</h2>
                <p className="text-sm text-gray-600 mt-1">Essential information about your temple</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Temple Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Temple Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="templeName"
                  value={formData.templeName}
                  onChange={handleInputChange}
                  placeholder="Enter temple name"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.templeName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.templeName && <p className="text-red-500 text-sm mt-1">{errors.templeName}</p>}
              </div>

              {/* Temple Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Temple Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="templeType"
                  value={formData.templeType}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.templeType ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all appearance-none cursor-pointer`}
                >
                  <option value="">Select temple type</option>
                  <option value="shiva">Shiva Temple</option>
                  <option value="vishnu">Vishnu Temple</option>
                  <option value="devi">Devi Temple</option>
                  <option value="ganesha">Ganesha Temple</option>
                  <option value="hanuman">Hanuman Temple</option>
                  <option value="other">Other</option>
                </select>
                {errors.templeType && <p className="text-red-500 text-sm mt-1">{errors.templeType}</p>}
              </div>

              {/* Main Deity Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Main Deity Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="mainDeityName"
                  value={formData.mainDeityName}
                  onChange={handleInputChange}
                  placeholder="Enter main deity name"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.mainDeityName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.mainDeityName && <p className="text-red-500 text-sm mt-1">{errors.mainDeityName}</p>}
              </div>

              {/* Year Established */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Year Established <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="yearEstablished"
                  value={formData.yearEstablished}
                  onChange={handleInputChange}
                  placeholder="YYYY"
                  maxLength="4"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.yearEstablished ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.yearEstablished && <p className="text-red-500 text-sm mt-1">{errors.yearEstablished}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-5 border-b border-orange-200">
            <div className="flex items-center gap-3">
              <Phone className="w-6 h-6 text-orange-700" />
              <div>
                <h2 className="text-xl font-bold text-orange-900">Contact Information</h2>
                <p className="text-sm text-gray-600 mt-1">How can people reach your temple</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Contact Person Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Contact Person Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contactPersonName"
                  value={formData.contactPersonName}
                  onChange={handleInputChange}
                  placeholder="Enter contact person name"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.contactPersonName ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.contactPersonName && <p className="text-red-500 text-sm mt-1">{errors.contactPersonName}</p>}
              </div>

              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.mobileNumber ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.mobileNumber && <p className="text-red-500 text-sm mt-1">{errors.mobileNumber}</p>}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleInputChange}
                  placeholder="temple@example.com"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.emailAddress ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.emailAddress && <p className="text-red-500 text-sm mt-1">{errors.emailAddress}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Location Details Section */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-5 border-b border-orange-200">
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-orange-700" />
              <div>
                <h2 className="text-xl font-bold text-orange-900">Location Details</h2>
                <p className="text-sm text-gray-600 mt-1">Where is your temple located</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-6">
              {/* Full Address */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Full Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullAddress"
                  value={formData.fullAddress}
                  onChange={handleInputChange}
                  placeholder="Enter complete address"
                  className={`w-full px-4 py-3 bg-gray-50 border ${errors.fullAddress ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                />
                {errors.fullAddress && <p className="text-red-500 text-sm mt-1">{errors.fullAddress}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* City */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                    className={`w-full px-4 py-3 bg-gray-50 border ${errors.city ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                  />
                  {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="Enter state"
                    className={`w-full px-4 py-3 bg-gray-50 border ${errors.state ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                  />
                  {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="6-digit pincode"
                    maxLength="6"
                    className={`w-full px-4 py-3 bg-gray-50 border ${errors.pincode ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all`}
                  />
                  {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>}
                </div>
              </div>

              {/* Google Map Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Google Map Location (URL or Coordinates)
                </label>
                <input
                  type="text"
                  name="googleMapLocation"
                  value={formData.googleMapLocation}
                  onChange={handleInputChange}
                  placeholder="https://maps.google.com/... or lat,lng"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Temple Information Section */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-5 border-b border-orange-200">
            <div className="flex items-center gap-3">
              <Info className="w-6 h-6 text-orange-700" />
              <div>
                <h2 className="text-xl font-bold text-orange-900">Temple Information</h2>
                <p className="text-sm text-gray-600 mt-1">Details about temple services and timings</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            {/* Temple Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Temple Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="templeDescription"
                value={formData.templeDescription}
                onChange={handleInputChange}
                placeholder="Describe your temple's history, significance, and features..."
                rows="4"
                className={`w-full px-4 py-3 bg-gray-50 border ${errors.templeDescription ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none`}
              />
              {errors.templeDescription && <p className="text-red-500 text-sm mt-1">{errors.templeDescription}</p>}
            </div>

            {/* Darshan Timings */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Darshan Timings <span className="text-red-500">*</span>
              </label>
              <textarea
                name="darshanTimings"
                value={formData.darshanTimings}
                onChange={handleInputChange}
                placeholder="e.g., Morning: 6:00 AM - 12:00 PM, Evening: 5:00 PM - 9:00 PM"
                rows="2"
                className={`w-full px-4 py-3 bg-gray-50 border ${errors.darshanTimings ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none`}
              />
              {errors.darshanTimings && <p className="text-red-500 text-sm mt-1">{errors.darshanTimings}</p>}
            </div>

            {/* Special Sevas / Pujas Offered */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Special Sevas / Pujas Offered
              </label>
              <textarea
                name="specialSevas"
                value={formData.specialSevas}
                onChange={handleInputChange}
                placeholder="List special sevas and pujas available at your temple..."
                rows="3"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Temple Rules or Instructions */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Temple Rules or Instructions
              </label>
              <textarea
                name="templeRules"
                value={formData.templeRules}
                onChange={handleInputChange}
                placeholder="Enter temple rules and guidelines for visitors..."
                rows="3"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
              />
            </div>
          </div>
        </div>

        {/* Media Upload Section */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-5 border-b border-orange-200">
            <div className="flex items-center gap-3">
              <ImageIcon className="w-6 h-6 text-orange-700" />
              <div>
                <h2 className="text-xl font-bold text-orange-900">Media Upload</h2>
                <p className="text-sm text-gray-600 mt-1">Update temple images (JPG/PNG only, max 5MB each)</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Temple Profile Image */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                Temple Profile Image (Optional - Keep current or upload new)
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="profileImage"
                  accept="image/jpeg,image/png"
                  onChange={handleProfileImageUpload}
                  className="hidden"
                />
                <label
                  htmlFor="profileImage"
                  className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-orange-300 rounded-2xl cursor-pointer bg-orange-50 hover:bg-orange-100 transition-all"
                >
                  {profilePreview ? (
                    <div className="relative w-full h-full p-4">
                      <img
                        src={profilePreview}
                        alt="Profile preview"
                        className="w-full h-full object-contain rounded-xl"
                      />
                      <div className="absolute top-6 right-6 bg-white rounded-full p-2 shadow-lg">
                        <Upload className="w-5 h-5 text-orange-600" />
                      </div>
                      {existingProfileImage && !formData.profileImage && (
                        <div className="absolute bottom-6 left-6 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">
                          Current Image
                        </div>
                      )}
                      {formData.profileImage && (
                        <div className="absolute bottom-6 left-6 bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                          New Image
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-center w-16 h-16 mb-4 bg-white rounded-full shadow-md">
                        <Upload className="w-8 h-8 text-orange-600" />
                      </div>
                      <p className="text-orange-700 font-semibold mb-2">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-gray-500 text-sm">JPG or PNG (max 5MB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Temple Gallery Images */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                Temple Gallery Images (Optional - Upload new images to replace, max 10 images)
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="galleryImages"
                  accept="image/jpeg,image/png"
                  multiple
                  onChange={handleGalleryImagesUpload}
                  className="hidden"
                />
                <label
                  htmlFor="galleryImages"
                  className="flex flex-col items-center justify-center w-full min-h-64 border-2 border-dashed border-orange-300 rounded-2xl cursor-pointer bg-orange-50 hover:bg-orange-100 transition-all p-4"
                >
                  {galleryPreviews.length > 0 ? (
                    <div className="w-full">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                        {galleryPreviews.map((preview, index) => (
                          <div key={index} className="relative aspect-square group">
                            <img
                              src={preview}
                              alt={`Gallery ${index + 1}`}
                              className="w-full h-full object-cover rounded-lg"
                            />
                            {existingGalleryImages.includes(preview) && (
                              <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                Current
                              </div>
                            )}
                            {formData.galleryImages.length > 0 && !existingGalleryImages.includes(preview) && (
                              <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                New
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-center py-4 border-t-2 border-dashed border-orange-300">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                          <p className="text-orange-700 font-semibold">Click to replace gallery images</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-center w-16 h-16 mb-4 bg-white rounded-full shadow-md">
                        <Upload className="w-8 h-8 text-orange-600" />
                      </div>
                      <p className="text-orange-700 font-semibold mb-2">
                        Click to upload multiple images
                      </p>
                      <p className="text-gray-500 text-sm">JPG or PNG (max 5MB each, up to 10 images)</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end sticky bottom-4 bg-white p-4 rounded-2xl shadow-xl border-2 border-orange-200">
          <button
            onClick={() => navigate('/temple-dashboard')}
            disabled={saving}
            className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={saving}
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Saving Changes...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TempleEditPage;